import React from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@material-ui/core';
import { toast } from 'react-toastify';

// Fixed "Other" entry returned by GET /api/Broadcast/recipient-groups; lets a Super Admin type addresses.
export const OTHER_RECIPIENT_GROUP_ID = -1;

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const hasOtherRecipient = (recipients) =>
    (recipients || []).some((group) => (group?.value ?? group?.id) === OTHER_RECIPIENT_GROUP_ID);

export const splitEmailEntries = (text = '') =>
    (text || '').split(/[\s,;]+/).map((entry) => entry.trim()).filter(Boolean);

export const countValidEmails = (text = '') =>
    new Set(splitEmailEntries(text).filter((entry) => EMAIL_REGEX.test(entry)).map((entry) => entry.toLowerCase())).size;

export const buildOtherRecipientOption = (recipientList = []) =>
    recipientList.find((group) => group.value === OTHER_RECIPIENT_GROUP_ID) || null;

// Reads the send response; field casing follows the API's JSON settings, so both casings are accepted.
export const getSendResult = (response) => {
    const entity = response?.data?.Entity || response?.data?.entity || null;
    if (!entity) return null;
    return {
        total: entity.totalRecipients ?? entity.TotalRecipients,
        delivered: entity.deliveredCount ?? entity.DeliveredCount,
        failed: entity.failedCount ?? entity.FailedCount,
        status: entity.status ?? entity.Status,
        invalidEmails: entity.invalidEmails ?? entity.InvalidEmails ?? [],
    };
};

// Shows the success/failure counts (and skipped invalid entries) after a send. Returns true when the send was processed.
export const reportSendResult = (response, { successMessage = 'Email sent successfully!', failureMessage = 'Failed to send email. Please try again.' } = {}) => {
    if (response?.data?.Status !== 200) {
        toast.error(response?.data?.Message?.AppStatusDescription || failureMessage);
        return false;
    }
    const result = getSendResult(response);
    const hasCounts = Number.isFinite(result?.delivered) && Number.isFinite(result?.failed);
    if (!hasCounts) {
        toast.success(successMessage);
    } else if (result.failed === 0) {
        toast.success(`${successMessage} Sent: ${result.delivered}, Failed: 0.`);
    } else if (result.delivered === 0) {
        toast.error(`Email sending failed. Sent: 0, Failed: ${result.failed}.`);
    } else {
        toast.warn(`Email sent with some failures. Sent: ${result.delivered}, Failed: ${result.failed}.`);
    }
    const invalid = result?.invalidEmails || [];
    if (invalid.length > 0) {
        toast.warn(`Skipped invalid email address(es): ${invalid.join(', ')}`, { autoClose: false });
    }
    return true;
};

export const OtherRecipientEmailsInput = ({ value, onChange, disabled }) => (
    <TextField
        variant="outlined"
        size="small"
        multiline
        minRows={3}
        fullWidth
        style={{ marginTop: 8, backgroundColor: '#fff' }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Type or paste email addresses separated by comma, semicolon, space or new line"
        inputProps={{ 'aria-label': 'Other recipient email addresses', 'data-testid': 'other-recipient-emails' }}
    />
);

OtherRecipientEmailsInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};
